import { reviewLocalBase, reviewRequest } from './reviewRequest';
import axios from 'axios';

import { requireLocalBackendUrl } from './localBackend';

import { API } from '@/config/constants';


const orderCollectionUrl = (params = {}) => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => query.append(key, item));
      return;
    }
    query.append(key, value);
  });
  const encoded = query.toString();
  return `${API}/orders${encoded ? `?${encoded}` : ''}`;
};


export const getOrders = async (params, config) => {
  const response = await axios.get(orderCollectionUrl(params), ...(config ? [config] : []));
  return response.data;
};

export const getPipelineOrders = async ({ date, stage }, config) => {
  const query = new URLSearchParams({ date, stage });
  const response = await axios.get(`${API}/orders/pipeline?${query}`, ...(config ? [config] : []));
  return response.data;
};

export const getOrder = async (orderId, config) => {
  const response = await axios.get(`${API}/orders/${orderId}`, ...(config ? [config] : []));
  return response.data;
};

export const getOrderNetAmount = async (orderId, inputs, config) => {
  const response = await axios.get(`${API}/orders/${encodeURIComponent(orderId)}/net-amount`, { ...config, params: inputs });
  return response.data;
};

export const getOrderNetAmounts = async (ids, config) => {
  const query = new URLSearchParams();
  ids.forEach(id => query.append('ids', id));
  const response = await axios.get(`${API}/orders/net-amounts?${query}`, config);
  return response.data;
};

export const createOrder = async (data, idempotencyKey, config = {}) => {
  const response = await axios.post(`${API}/orders`, data, {
    ...config,
    headers: { ...config.headers, 'Idempotency-Key': idempotencyKey },
  });
  return response.data;
};

export const createLocalOrder = async (
  data,
  idempotencyKey,
  submissionState = 'pending_review',
  config = {},
) => {
  const localBackendUrl = requireLocalBackendUrl();
  const response = await axios.post(
    `${localBackendUrl}/api/desktop/orders`,
    data,
    {
      ...config,
      headers: {
        ...config.headers,
        'Idempotency-Key': idempotencyKey,
        'X-LVB-Submission-State': submissionState,
      },
    },
  );
  return response.data;
};

const localOrderUrl = (orderId) => {
  const localBackendUrl = requireLocalBackendUrl();
  return `${localBackendUrl}/api/desktop/orders/${orderId}`;
};

export const updateLocalOrder = async (orderId, data, config) => {
  const response = await axios.put(localOrderUrl(orderId), data, ...(config ? [config] : []));
  return response.data;
};

export const placeLocalOrderOnOfflineMap = async (orderId, placement, config) => {
  const response = await axios.post(
    `${localOrderUrl(orderId)}/offline-map-placement`,
    placement,
    ...(config ? [config] : []),
  );
  return response.data;
};

export const clearLocalOrderOfflineMapPlacement = async (orderId, config) => {
  const response = await axios.delete(
    `${localOrderUrl(orderId)}/offline-map-placement`,
    ...(config ? [config] : []),
  );
  return response.data;
};

export const deleteLocalOrder = async (orderId, config) => {
  const response = await axios.delete(localOrderUrl(orderId), ...(config ? [config] : []));
  return response.data;
};

// By-ID observation includes pending offline deletion evidence hidden by lists.
export const getLocalOrder = async (orderId, config) => {
  const response = await axios.get(`${requireLocalBackendUrl()}/api/orders/${orderId}`, ...(config ? [config] : []));
  return response.data;
};

const reviewLocal = (path, config) => `${reviewLocalBase(config)}/api/desktop/reconciliation/${path}`;
export const beginCachedEditDecision = (id, revision, decision, config, reviewedCloud) => reviewRequest('post', reviewLocal(`edit-decisions/${id}/begin`, config), { revision, decision, reviewed_cloud: reviewedCloud }, config);
export const settleCachedEditDecision = (id, attempt, cloudOrder, refused, config) => reviewRequest('post', reviewLocal(`edit-decisions/${id}/settle`, config), { attempt, cloud_order: cloudOrder, refused }, config);
export const getOfflineEditCapabilities = config => reviewRequest('get', `${API}/offline-edit-capabilities`, undefined, config);
export const getLocalReconciliationQueue = config => reviewRequest('get', reviewLocal('orders', config), undefined, config);
export const validateManualOrder = (data, config) => reviewRequest('post', `${API}/orders/validate-manual`, data, config);
export const completeLocalReconciliation = (id, cloudOrder, config) => reviewRequest('post', reviewLocal(`orders/${id}/complete`, config), { cloud_order: cloudOrder }, config);
export const discardLocalReconciliationOrder = (id, config) => reviewRequest('post', reviewLocal(`orders/${id}/discard`, config), undefined, config);
export const getCachedOrderDeleteRequests = config => reviewRequest('get', reviewLocal('cached-order-delete-requests', config), undefined, config);
export const applyCachedOrderDeleteRequest = (mutation, config) => {
  if (!mutation?.target_id) throw new Error('Review target order is unavailable');
  return reviewRequest('post', `${API}/orders/${mutation.target_id}/resolve-delete-mutation-conflict`, mutation, config);
};
export const resolveCachedOrderDeleteRequest = (id, resolution, cloudOrder, config) => reviewRequest('post', reviewLocal(`cached-order-delete-requests/${id}/resolve`, config), { resolution, cloud_order: cloudOrder }, config);
export const getCachedOrderStatusConflicts = config => reviewRequest('get', reviewLocal('cached-order-status-conflicts', config), undefined, config);
export const applyCachedOrderStatusConflict = (mutation, config) => {
  if (!mutation?.target_id) throw new Error('Review target order is unavailable');
  return reviewRequest('post', `${API}/orders/${mutation.target_id}/resolve-status-mutation-conflict`, mutation, config);
};
export const resolveCachedOrderStatusConflict = (id, resolution, cloudOrder, config) => reviewRequest('post', reviewLocal(`cached-order-status-conflicts/${id}/resolve`, config), { resolution, cloud_order: cloudOrder }, config);
export const refreshCachedOrderStatusConflictEvidence = (id, cloudOrder, config) => reviewRequest('post', reviewLocal(`cached-order-status-conflicts/${id}/refresh-evidence`, config), { cloud_order: cloudOrder }, config);
export const getCachedOrderFieldConflicts = config => reviewRequest('get', reviewLocal('cached-order-field-conflicts', config), undefined, config);
export const applyCachedOrderFieldConflict = (mutation, config) => {
  if (!mutation?.target_id) throw new Error('Review target order is unavailable');
  return reviewRequest('post', `${API}/orders/${mutation.target_id}/resolve-field-mutation-conflict`, mutation, config);
};
export const resolveCachedOrderFieldConflict = (id, resolution, cloudOrder, config) => reviewRequest('post', reviewLocal(`cached-order-field-conflicts/${id}/resolve`, config), { resolution, cloud_order: cloudOrder }, config);
export const refreshCachedOrderFieldConflictEvidence = (id, cloudOrder, config) => reviewRequest('post', reviewLocal(`cached-order-field-conflicts/${id}/refresh-evidence`, config), { cloud_order: cloudOrder }, config);

export const createCachedOrderFieldMutation = async (orderId, data, config) => {
  const localBackendUrl = requireLocalBackendUrl();
  const response = await axios.post(
    `${localBackendUrl}/api/desktop/orders/${orderId}/field-mutation`,
    data,
    ...(config ? [config] : []),
  );
  return response.data;
};

export const submitManualOrder = async (
  data,
  idempotencyKey,
  { offline = false, desktopApp = false, requestConfig = {}, localRequestConfig = {},
    isCurrent = () => true, recoveryTarget = null } = {},
) => {
  if (!isCurrent()) {
    const error = new Error('Submission session is no longer current');
    error.manualOutcome = 'not_submitted';
    throw error;
  }
  const preserveUncertain = async () => {
    try {
      const order = await createLocalOrder(
        data, idempotencyKey, 'submission_uncertain', localRequestConfig,
      );
      if (!order?.id) throw new Error('No usable local receipt');
      return { order, outcome: 'submission_uncertain' };
    } catch {
      const error = new Error('Cloud outcome unknown; local preservation could not be confirmed');
      error.manualOutcome = 'uncertain';
      error.localPreservation = 'failed';
      throw error;
    }
  };
  // A lost local reply may still mean Review owns a durable proposal. Recover
  // that local receipt first; never return to direct cloud creation on retry.
  if (recoveryTarget === 'local_uncertain') {
    if (desktopApp) return preserveUncertain();
    const error = new Error('Local preservation must be recovered on the original desktop');
    error.manualOutcome = 'uncertain';
    error.localPreservation = 'failed';
    throw error;
  }
  if (offline) {
    return {
      order: await createLocalOrder(data, idempotencyKey, 'pending_review', localRequestConfig),
      outcome: 'saved_locally',
    };
  }

  try {
    return {
      order: await createOrder(data, idempotencyKey, requestConfig),
      outcome: 'created_in_cloud',
    };
  } catch (cloudError) {
    const responseMissing = !cloudError.response && (
      cloudError.request
      || ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(
        cloudError.code,
      )
    );
    if (!desktopApp || !responseMissing) throw cloudError;
    // The cloud may already have accepted. Never use a replacement account's
    // ambient credential for the secondary local write.
    if (!isCurrent()) {
      const error = new Error('Cloud outcome unknown; local preservation was not attempted');
      error.manualOutcome = 'uncertain';
      error.localPreservation = 'not_attempted';
      throw error;
    }
    return preserveUncertain();
  }
};

export const updateOrder = async (orderId, data, config) => {
  const response = await axios.put(`${API}/orders/${orderId}`, data, ...(config ? [config] : []));
  return response.data;
};

export const uploadOrderMedia = async (orderId, files, config = {}) => {
  const form = new FormData();
  files.forEach((file) => form.append('photos', file));
  const response = await axios.post(`${API}/orders/${orderId}/media`, form, {
    ...config,
    headers: { ...config.headers, 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

export const deleteOrderMedia = async (orderId, storagePath, category, config = {}) => {
  const mediaType = category === 'delivery' ? 'delivery-media' : 'media';
  const response = await axios.delete(`${API}/orders/${orderId}/${mediaType}`, {
    ...config,
    headers: { ...config.headers, 'Content-Type': 'application/json' },
    data: { storage_path: storagePath },
  });
  return response.data;
};

export const getOrderSourceResolution = async (orderId, config) => {
  const response = await axios.get(`${API}/orders/${orderId}/resolve-source`, ...(config ? [config] : []));
  return response.data;
};

export const getOrderSubsourceResolution = async (orderId, config) => {
  const response = await axios.get(`${API}/orders/${orderId}/resolve-subsource`, ...(config ? [config] : []));
  return response.data;
};

export const getOrderProductResolution = async (orderId, config) => {
  const response = await axios.get(`${API}/orders/${orderId}/resolve-product`, ...(config ? [config] : []));
  return response.data;
};

export const getOrderCommitmentStatus = async (orderId, { local = false, ...config } = {}) => {
  const base = local ? localOrderUrl(orderId) : `${API}/orders/${orderId}`;
  const response = await axios.get(`${base}/commitment-status`, ...(Object.keys(config).length ? [config] : []));
  return response.data;
};

export const deleteOrder = async (orderId, confirmPassword, config) => {
  const url = `${API}/orders/${orderId}`;
  const response = confirmPassword
    ? await axios.delete(url, { ...config, headers: { ...config?.headers, 'X-Confirm-Password': confirmPassword } })
    : await axios.delete(url, ...(config ? [config] : []));
  return response.data;
};

export const assignOrderDesigner = async (orderId, designerId, config) => {
  const response = await axios.patch(
    `${API}/orders/${orderId}/assign`,
    { designer_id: designerId },
    ...(config ? [config] : []),
  );
  return response.data;
};

export const markOrderDesigned = async (orderId, config) => {
  const response = await axios.post(`${API}/orders/${orderId}/mark-designed`, null, ...(config ? [config] : []));
  return response.data;
};

export const markLocalOrderDesigned = async (orderId, config) => {
  const localBackendUrl = requireLocalBackendUrl();
  const response = await axios.post(
    `${localBackendUrl}/api/desktop/orders/${orderId}/mark-designed`,
    null,
    ...(config ? [config] : []),
  );
  return response.data;
};

// Offline, a deletion is a request rather than the act: the order lives in the
// cloud, so the desktop records the intent and a person approves it at Review.
// See TCA-26081406.
export const createCachedOrderDeleteMutation = async (orderId, config) => {
  const localBackendUrl = requireLocalBackendUrl();
  const response = await axios.post(
    `${localBackendUrl}/api/desktop/orders/${orderId}/delete-mutation`,
    null,
    ...(config ? [config] : []),
  );
  return response.data;
};

export const createCachedOrderStatusMutation = async (orderId, status, config, editContext) => {
  const localBackendUrl = requireLocalBackendUrl();
  const response = await axios.post(
    `${localBackendUrl}/api/desktop/orders/${orderId}/status-mutation`,
    { status, ...(editContext ? { edit_context: editContext } : {}) },
    ...(config ? [config] : []),
  );
  return response.data;
};

export const acknowledgeOrder = async (orderId, config) => {
  const response = await axios.post(`${API}/orders/${orderId}/acknowledge`, ...(config ? [null, config] : []));
  return response.data;
};

export const setOrderDuplicateResolution = async (orderId, resolution, config) => {
  const response = await axios.patch(
    `${API}/orders/${orderId}/duplicate-resolution`,
    { resolution },
    ...(config ? [config] : []),
  );
  return response.data;
};
